import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAcceptanceList } from '@saiens/shared/hooks';
import { formatDate } from '@saiens/shared/utils';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Search, ChevronRight, ArrowLeft } from 'lucide-react';

const STATE_LABELS: Record<string, string> = {
  draft: '草稿',
  signed: '已簽名',
  submitted: '已送出',
  invoiced: '已開帳',
  done: '完成',
};

const STATE_COLORS: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  signed: 'bg-yellow-100 text-yellow-700',
  submitted: 'bg-blue-100 text-blue-700',
  invoiced: 'bg-green-100 text-green-700',
  done: 'bg-green-200 text-green-800',
};

export function AcceptanceManagePage() {
  const [search, setSearch] = useState('');
  const [state, setState] = useState('');
  const [myOrders, setMyOrders] = useState(false);

  const { data, isLoading, error } = useAcceptanceList({
    search,
    state: state || undefined,
    myOrders: myOrders || undefined,
    limit: 50,
  });

  return (
    <div className="px-4 py-6">
      <div className="mb-4 flex items-center gap-3">
        <Link to="/acceptance" className="text-gray-500">
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-xl font-bold text-gray-900">驗收管理</h1>
          <p className="text-sm text-gray-500">所有驗收單</p>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="mb-4 space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="搜尋..."
            className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-10 pr-4 text-sm focus:border-primary focus:outline-none"
          />
        </div>

        <div className="flex gap-2 overflow-x-auto">
          <FilterChip
            label="我的"
            active={myOrders}
            onClick={() => setMyOrders(!myOrders)}
          />
          <FilterChip
            label="全部"
            active={!state}
            onClick={() => setState('')}
          />
          <FilterChip
            label="草稿"
            active={state === 'draft'}
            onClick={() => setState('draft')}
          />
          <FilterChip
            label="已簽名"
            active={state === 'signed'}
            onClick={() => setState('signed')}
          />
          <FilterChip
            label="已送出"
            active={state === 'submitted'}
            onClick={() => setState('submitted')}
          />
          <FilterChip
            label="已開帳"
            active={state === 'invoiced'}
            onClick={() => setState('invoiced')}
          />
          <FilterChip
            label="完成"
            active={state === 'done'}
            onClick={() => setState('done')}
          />
        </div>
      </div>

      {/* List */}
      <div className="rounded-xl bg-white shadow-sm">
        {isLoading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : error ? (
          <div className="py-12 text-center text-red-500">載入失敗</div>
        ) : data?.items.length === 0 ? (
          <div className="py-12 text-center text-gray-500">沒有資料</div>
        ) : (
          <div className="divide-y">
            {data?.items.map((acc) => (
              <Link
                key={acc.id}
                to={`/acceptance/${acc.id}`}
                className="flex items-center justify-between p-4 hover:bg-gray-50"
              >
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-gray-900">{acc.name}</p>
                  <p className="truncate text-sm text-gray-500">
                    {acc.project_name || acc.sale_order_name} &bull;{' '}
                    {acc.partner_name || '---'}
                  </p>
                  <div className="mt-1 flex items-center gap-2 text-xs text-gray-400">
                    <span>{formatDate(acc.completion_date)}</span>
                    {acc.employee_name && (
                      <span>&bull; {acc.employee_name}</span>
                    )}
                    <span>&bull; 分數: {acc.total_score.toFixed(1)}</span>
                  </div>
                </div>
                <div className="ml-4 flex items-center gap-3">
                  <span
                    className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${STATE_COLORS[acc.state] || 'bg-gray-100 text-gray-700'}`}
                  >
                    {STATE_LABELS[acc.state] || acc.state}
                  </span>
                  <ChevronRight className="h-5 w-5 text-gray-300" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {data && (
        <p className="mt-4 text-center text-sm text-gray-500">
          共 {data.total} 筆
        </p>
      )}
    </div>
  );
}

function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
        active
          ? 'bg-primary text-white'
          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
      }`}
    >
      {label}
    </button>
  );
}
